import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Calendar, Clock, BookOpen, Sparkles, SearchX, Scale,
  ArrowUpDown, History, Moon, Download, UserCheck, CalendarDays,
  Settings, Shield, Megaphone, Calculator, BarChart2, FileText,
  RotateCcw, ChevronDown, ChevronRight, RefreshCw, Package
} from 'lucide-react';
import { CUSTOMERS, EMPLOYEES } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PageHeader({ icon: Icon, section, title, subtitle }) {
  return (
    <div className="mb-6">
      <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5"><Icon size={11} /> {section}</div>
      <h1 className="font-black text-3xl tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
}

function PlaceholderTable({ columns, rowCount = 5 }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {columns.map(c => (
              <th key={c} className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rowCount }).map((_, i) => (
            <tr key={i} className="border-b border-gray-100">
              {columns.map(c => (
                <td key={c} className="px-4 py-3">
                  <div className="h-3 bg-gray-100 rounded animate-pulse" style={{ width: `${55 + (i * 13 + c.length * 7) % 35}%` }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">Loading data…</div>
    </div>
  );
}

/* ─── Calendar & Schedule ───────────────────────────────────── */
export function CalendarPage() {
  const { authHeader } = useAuth();
  const today = new Date();
  const [viewDate, setViewDate]   = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [salesMap, setSalesMap]   = useState({});
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/sales`, { headers: authHeader() })
      .then(r => {
        const map = {};
        (r.data || []).forEach(s => {
          const d = s.date || '';
          if (d) map[d] = (map[d] || 0) + 1;
        });
        setSalesMap(map);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const { year, month } = viewDate;
  const monthNames = ['January','February','March','April','May','June',
                      'July','August','September','October','November','December'];
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const firstDay   = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = Array(firstDay).fill(null).concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  const pad = n => String(n).padStart(2, '0');
  const isToday = day => day && today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

  const prev = () => setViewDate(v => v.month === 0 ? { year: v.year - 1, month: 11 } : { year: v.year, month: v.month - 1 });
  const next = () => setViewDate(v => v.month === 11 ? { year: v.year + 1, month: 0 } : { year: v.year, month: v.month + 1 });

  const totalThisMonth = Object.entries(salesMap)
    .filter(([d]) => d.startsWith(`${year}-${pad(month + 1)}`))
    .reduce((s, [, v]) => s + v, 0);

  return (
    <div className="px-6 py-6 max-w-3xl">
      <div className="flex items-start justify-between mb-5">
        <PageHeader icon={Calendar} section="Team" title="Calendar & Schedule" subtitle="Scheduled orders and deliveries" />
        <div className="self-start mt-6 text-xs text-gray-400">{loading ? 'Loading…' : `${totalThisMonth} sales this month`}</div>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="font-black text-lg">{monthNames[month]} {year}</div>
          <div className="flex gap-2">
            <button onClick={prev} className="px-3 py-1.5 border border-gray-200 rounded text-sm hover:bg-gray-50">‹</button>
            <button onClick={() => setViewDate({ year: today.getFullYear(), month: today.getMonth() })}
              className="px-3 py-1.5 border border-gray-200 rounded text-xs hover:bg-gray-50">Today</button>
            <button onClick={next} className="px-3 py-1.5 border border-gray-200 rounded text-sm hover:bg-gray-50">›</button>
          </div>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-7 gap-1 mb-1">
            {days.map(d => <div key={d} className="text-center small-label text-gray-400 py-2">{d}</div>)}
          </div>
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 gap-1">
              {week.map((day, di) => {
                const key = day ? `${year}-${pad(month + 1)}-${pad(day)}` : '';
                const cnt = key ? salesMap[key] || 0 : 0;
                return (
                  <div key={di} className={`min-h-[52px] rounded-md p-1.5 text-[13px] transition-colors ${
                    isToday(day) ? 'bg-black text-white' : day ? 'hover:bg-gray-50 cursor-pointer' : ''
                  }`}>
                    {day && (
                      <>
                        <div className="font-semibold">{day}</div>
                        {cnt > 0 && (
                          <div className={`mt-1 text-[10px] rounded px-1 py-0.5 font-semibold ${
                            isToday(day) ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {cnt} sale{cnt > 1 ? 's' : ''}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Stock Movement ────────────────────────────────────────── */
export function StockMovement() {
  const { authHeader } = useAuth();
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('ALL');

  useEffect(() => {
    axios.get(`${API}/stock-movement`, { headers: authHeader() })
      .then(r => setData(r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const TYPE_COLOR = {
    PURCHASE: 'text-green-700 bg-green-50', SALE: 'text-red-700 bg-red-50',
    TRANSFER: 'text-blue-700 bg-blue-50', DEFECTIVE: 'text-orange-700 bg-orange-50',
    'ADJ+': 'text-green-700 bg-green-50', 'ADJ-': 'text-red-700 bg-red-50',
    'MT RETURN': 'text-blue-700 bg-blue-50', 'MT DISPATCH': 'text-amber-700 bg-amber-50',
  };
  const types = ['ALL', ...new Set(data.map(r => r.type))];
  const filtered = filter === 'ALL' ? data : data.filter(r => r.type === filter);

  return (
    <div className="px-6 py-6">
      <PageHeader icon={ArrowUpDown} section="Reports" title="Stock Movement" subtitle="In/out movement history" />
      <div className="flex flex-wrap gap-1 mb-4">
        {types.map(t => (
          <button key={t} onClick={() => setFilter(t)}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all ${
              filter === t ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-500 hover:border-gray-400'
            }`}>{t}</button>
        ))}
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {['Date','Godown','Brand','Size','Type','Qty','KG','Reference'].map(h => (
                  <th key={h} className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-10"><RefreshCw size={16} className="animate-spin text-gray-400 mx-auto" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-gray-400 text-[13px]">No movement data yet.</td></tr>
              ) : filtered.slice(0, 200).map((r, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-[13px] text-gray-600 whitespace-nowrap">{r.date}</td>
                  <td className="px-4 py-3 text-[13px] max-w-[150px] truncate">{r.godown}</td>
                  <td className="px-4 py-3 text-[13px] font-medium">{r.brand}</td>
                  <td className="px-4 py-3 text-[13px]">{r.size}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${TYPE_COLOR[r.type] || 'text-gray-600 bg-gray-50'}`}>{r.type}</span>
                  </td>
                  <td className={`px-4 py-3 text-[13px] font-semibold ${r.qty >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {r.qty >= 0 ? '+' : ''}{r.qty}
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-600">{r.kg ? (r.kg >= 0 ? '+' : '') + r.kg : '—'}</td>
                  <td className="px-4 py-3 text-[12px] text-gray-400 max-w-[120px] truncate">{r.reference || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">{filtered.length} movements</div>
      </div>
    </div>
  );
}

/* ─── Adjustment Log ────────────────────────────────────────── */
export function AdjustmentLog() {
  const { authHeader } = useAuth();
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/adjustments`, { headers: authHeader() })
      .then(r => setData(r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const REASON_LABEL = { defective: 'Defective', adj_plus: 'Adj +', adj_minus: 'Adj −',
                          mt_return: 'MT Return', mt_dispatch: 'MT Dispatch' };
  const REASON_COLOR = { defective: 'text-red-600', adj_plus: 'text-green-600', adj_minus: 'text-red-600',
                          mt_return: 'text-blue-600', mt_dispatch: 'text-amber-600' };

  const rows = [];
  data.forEach(adj => {
    (adj.items || []).forEach(item => {
      rows.push({ date: adj.date, godown: adj.godown, notes: adj.notes,
                  brand: item.brand, size: item.size, reason: item.reason, qty: item.qty });
    });
  });

  return (
    <div className="px-6 py-6">
      <PageHeader icon={History} section="Reports" title="Adjustment Log" subtitle="History of stock adjustments" />
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {['Date','Godown','Brand','Size','Type','Qty','Notes'].map(h => (
                  <th key={h} className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-10"><RefreshCw size={16} className="animate-spin text-gray-400 mx-auto" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-400 text-[13px]">No adjustments recorded yet.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-[13px] text-gray-600 whitespace-nowrap">{r.date}</td>
                  <td className="px-4 py-3 text-[13px]">{r.godown}</td>
                  <td className="px-4 py-3 text-[13px] font-medium">{r.brand}</td>
                  <td className="px-4 py-3 text-[13px]">{r.size}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-bold ${REASON_COLOR[r.reason] || 'text-gray-600'}`}>
                      {REASON_LABEL[r.reason] || r.reason}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[13px] font-semibold">{r.qty}</td>
                  <td className="px-4 py-3 text-[12px] text-gray-400 max-w-[180px] truncate">{r.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">{rows.length} entries</div>
      </div>
    </div>
  );
}

/* ─── Daily Closing ────────────────────────────────────────── */
export function DailyClosing() {
  const { authHeader } = useAuth();
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/daily-closing`, { headers: authHeader() })
      .then(r => setData(r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const fmtINR = n => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="px-6 py-6">
      <PageHeader icon={Moon} section="Reports" title="Daily Closing" subtitle="End-of-day summary reports" />
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {['Date','Sales','Total Sales ₹','KG Sold','Purchases','Total Purchase ₹'].map(h => (
                  <th key={h} className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-10"><RefreshCw size={16} className="animate-spin text-gray-400 mx-auto" /></td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-gray-400 text-[13px]">No data recorded yet.</td></tr>
              ) : data.map((r, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-[13px] font-medium whitespace-nowrap">{r.date}</td>
                  <td className="px-4 py-3 text-[13px]">{r.sales}</td>
                  <td className="px-4 py-3 text-[13px] font-semibold text-green-700">{fmtINR(r.total_sales)}</td>
                  <td className="px-4 py-3 text-[13px]">{r.total_kg.toFixed(1)} kg</td>
                  <td className="px-4 py-3 text-[13px]">{r.purchases}</td>
                  <td className="px-4 py-3 text-[13px] text-blue-700">{fmtINR(r.total_purchases)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">{data.length} days</div>
      </div>
    </div>
  );
}

/* ─── Downloads ────────────────────────────────────────────── */
export function Downloads() {
  const { authHeader } = useAuth();
  const [busy, setBusy] = useState('');

  const exportCSV = async (name, endpoint, transform) => {
    setBusy(name);
    try {
      const r = await axios.get(`${API}${endpoint}`, { headers: authHeader() });
      const rows = transform(r.data);
      if (!rows.length) { alert('No data to export.'); return; }
      const headers = Object.keys(rows[0]);
      const csv = [headers.join(','),
        ...rows.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(','))
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${name.replace(/ /g, '_')}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Export failed — try again.'); }
    finally { setBusy(''); }
  };

  const reports = [
    { label: 'Monthly Sales Report',   endpoint: '/sales',            transform: rows => rows.map(r => ({ date: r.date, challan: r.challan_no, customer: r.customer_name, godown: r.godown, total_kg: r.total_kg, total_amount: r.total_amount })) },
    { label: 'Purchase Ledger',         endpoint: '/purchases',        transform: rows => rows.map(r => ({ date: r.date, challan: r.challan_no, vendor: r.vendor_name, godown: r.godown, total_amount: r.total_amount })) },
    { label: 'Payments Summary',        endpoint: '/payments',         transform: rows => rows.map(r => ({ date: r.date, party: r.party_name, type: r.payment_type, mode: r.mode, amount: r.amount })) },
    { label: 'Customer Statement',      endpoint: '/pending-payments', transform: rows => rows.map(r => ({ customer: r.customer_name, phone: r.phone, billed: r.total_billed, paid: r.total_paid, outstanding: r.outstanding })) },
    { label: 'Stock Movement Report',   endpoint: '/stock-movement',   transform: rows => rows.map(r => ({ date: r.date, godown: r.godown, brand: r.brand, size: r.size, type: r.type, qty: r.qty, kg: r.kg })) },
    { label: 'Daily Closing Summary',   endpoint: '/daily-closing',    transform: rows => rows.map(r => ({ date: r.date, sales: r.sales, total_sales: r.total_sales, total_kg: r.total_kg, purchases: r.purchases, total_purchases: r.total_purchases })) },
    { label: 'Adjustment Log',          endpoint: '/adjustments',      transform: rows => rows.flatMap(adj => (adj.items || []).map(i => ({ date: adj.date, godown: adj.godown, brand: i.brand, size: i.size, reason: i.reason, qty: i.qty }))) },
    { label: 'Rate Card',               endpoint: '/customers',        transform: rows => rows.filter(c => c.customer_type === 'customer').map(r => ({ name: r.name, phone: r.phone, rate_per_kg: r.current_rate, address: r.address })) },
  ];

  return (
    <div className="px-6 py-6 max-w-2xl">
      <PageHeader icon={Download} section="Reports" title="Downloads" subtitle="Export reports as CSV" />
      <div className="space-y-2">
        {reports.map((rpt) => (
          <div key={rpt.label} className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
            <div className="text-[13px] font-medium text-gray-800">{rpt.label}</div>
            <button
              onClick={() => exportCSV(rpt.label, rpt.endpoint, rpt.transform)}
              disabled={busy === rpt.label}
              className="text-[12px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 disabled:opacity-50"
            >
              {busy === rpt.label ? <RefreshCw size={11} className="animate-spin" /> : <Download size={12} />}
              {busy === rpt.label ? 'Exporting…' : 'CSV'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Attendance ───────────────────────────────────────────── */
export function AttendancePage() {
  const { authHeader } = useAuth();
  const todayStr = new Date().toISOString().split('T')[0];
  const [date,      setDate]      = useState(todayStr);
  const [employees, setEmployees] = useState([]);
  const [records,   setRecords]   = useState({});
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);

  // Load employees from Masters
  useEffect(() => {
    axios.get(`${API}/masters`, { headers: authHeader() })
      .then(r => {
        const emps = r.data.employees || [];
        setEmployees(emps);
        const init = {};
        emps.forEach(e => { init[e.id] = { employee_id: e.id, employee_name: e.name, status: 'Present', check_in: '', check_out: '', notes: '' }; });
        setRecords(init);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Load saved records for selected date
  useEffect(() => {
    if (!employees.length) return;
    axios.get(`${API}/attendance?date=${date}`, { headers: authHeader() })
      .then(r => {
        const existing = r.data || [];
        if (existing.length) {
          const map = {};
          existing.forEach(rec => { map[rec.employee_id] = rec; });
          setRecords(prev => {
            const upd = { ...prev };
            employees.forEach(e => { if (map[e.id]) upd[e.id] = map[e.id]; });
            return upd;
          });
        } else {
          // reset to Present for new date
          const init = {};
          employees.forEach(e => { init[e.id] = { employee_id: e.id, employee_name: e.name, status: 'Present', check_in: '', check_out: '', notes: '' }; });
          setRecords(init);
        }
      })
      .catch(console.error);
  }, [date, employees]);

  const updateRecord = (empId, field, value) => {
    setRecords(prev => ({ ...prev, [empId]: { ...prev[empId], [field]: value } }));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/attendance/mark`, { date, records: Object.values(records) }, { headers: authHeader() });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch { alert('Save failed'); } finally { setSaving(false); }
  };

  const STATUS_COLORS = { Present: 'bg-green-100 text-green-700', Absent: 'bg-red-100 text-red-700', 'Half-day': 'bg-amber-100 text-amber-700' };

  return (
    <div className="px-6 py-6" data-testid="attendance-page">
      <div className="flex items-start justify-between mb-5">
        <PageHeader icon={UserCheck} section="HR" title="Attendance" subtitle="Mark daily employee attendance" />
        <div className="flex items-center gap-3 self-start mt-6">
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:border-gray-400 bg-white" />
          <button data-testid="save-attendance-btn" onClick={saveAll} disabled={saving || !employees.length}
            className="btn-primary text-sm px-4 py-2 flex items-center gap-1.5">
            {saving ? <RefreshCw size={13} className="animate-spin" /> : saved ? '✓ Saved!' : 'Save Attendance'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10"><RefreshCw size={16} className="animate-spin text-gray-400 mx-auto" /></div>
      ) : employees.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <div className="text-gray-400 text-[13px]">No employees found.</div>
          <div className="text-gray-400 text-xs mt-1">Add employees in <strong>Masters</strong> first.</div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {['Employee','Role','Status','Check In','Check Out','Notes'].map(h => (
                    <th key={h} className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => {
                  const rec = records[emp.id] || { status: 'Present', check_in: '', check_out: '', notes: '' };
                  return (
                    <tr key={emp.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 text-[13px] font-medium">{emp.name}</td>
                      <td className="px-4 py-3 text-[12px] text-gray-400">{emp.role || '—'}</td>
                      <td className="px-4 py-3">
                        <select value={rec.status} onChange={e => updateRecord(emp.id, 'status', e.target.value)}
                          className={`text-[12px] font-semibold px-2 py-1 rounded border-0 focus:ring-0 cursor-pointer ${STATUS_COLORS[rec.status] || 'bg-gray-100 text-gray-600'}`}>
                          <option>Present</option><option>Absent</option><option>Half-day</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <input type="time" value={rec.check_in || ''} onChange={e => updateRecord(emp.id, 'check_in', e.target.value)}
                          className="px-2 py-1 border border-gray-200 rounded text-[12px] w-28 focus:outline-none focus:border-gray-400" />
                      </td>
                      <td className="px-4 py-3">
                        <input type="time" value={rec.check_out || ''} onChange={e => updateRecord(emp.id, 'check_out', e.target.value)}
                          className="px-2 py-1 border border-gray-200 rounded text-[12px] w-28 focus:outline-none focus:border-gray-400" />
                      </td>
                      <td className="px-4 py-3">
                        <input value={rec.notes || ''} onChange={e => updateRecord(emp.id, 'notes', e.target.value)}
                          className="px-2 py-1 border border-gray-200 rounded text-[12px] w-32 focus:outline-none focus:border-gray-400" placeholder="Optional" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">{employees.length} employees · {date}</div>
        </div>
      )}
    </div>
  );
}

/* ─── Monthly Summary ──────────────────────────────────────── */
export function MonthlySummary() {
  const { authHeader } = useAuth();
  const today = new Date();
  const [month, setMonth] = useState(`${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}`);
  const [data,  setData]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/attendance/summary?month=${month}`, { headers: authHeader() })
      .then(r => setData(r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [month]);

  const total_days = data.reduce((s, r) => Math.max(s, r.total), 0);

  return (
    <div className="px-6 py-6" data-testid="monthly-summary-page">
      <div className="flex items-start justify-between mb-5">
        <PageHeader icon={CalendarDays} section="HR" title="Monthly Summary" subtitle="Monthly attendance totals" />
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          className="self-start mt-6 px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:border-gray-400 bg-white" />
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {['Employee','Present','Half-day','Absent','Total Days','%'].map(h => (
                <th key={h} className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-10"><RefreshCw size={16} className="animate-spin text-gray-400 mx-auto" /></td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-400 text-[13px]">No attendance recorded for this month.</td></tr>
            ) : data.map((r, i) => {
              const pct = r.total ? Math.round(((r.present + r.half_day * 0.5) / r.total) * 100) : 0;
              return (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-[13px] font-medium">{r.employee_name}</td>
                  <td className="px-4 py-3 text-[13px] font-semibold text-green-700">{r.present}</td>
                  <td className="px-4 py-3 text-[13px] text-amber-600">{r.half_day}</td>
                  <td className="px-4 py-3 text-[13px] text-red-600">{r.absent}</td>
                  <td className="px-4 py-3 text-[13px]">{r.total}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gray-900 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[12px] font-semibold">{pct}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">{data.length} employees</div>
      </div>
    </div>
  );
}

/* ─── Masters ──────────────────────────────────────────────── */
export function MastersPage() {
  const { authHeader, company, role } = useAuth();
  const isAdmin = role === 'admin';
  const [tab,     setTab]    = useState('brands');
  const [data,    setData]   = useState({ brands: [], employees: [], sizes: [] });
  const [loading, setLoading] = useState(true);
  const [error,   setError]  = useState(null);
  const [saving,  setSaving] = useState(false);
  const [newBrand, setNewBrand] = useState('');
  const [newSize,  setNewSize]  = useState('');
  const [newEmp,   setNewEmp]   = useState({ name: '', role: '', phone: '' });

  const load = () => {
    setLoading(true);
    axios.get(`${API}/masters`, { headers: authHeader() })
      .then(r => { setData(r.data); setError(null); })
      .catch(() => setError('Failed to load.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const addBrand = async (e) => {
    e.preventDefault(); if (!newBrand.trim()) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/masters/brands`, { name: newBrand.trim() }, { headers: authHeader() });
      setData(d => ({ ...d, brands: [...d.brands, r.data] }));
      setNewBrand('');
    } catch { alert('Error adding brand'); } finally { setSaving(false); }
  };

  const delBrand = async (id) => {
    if (!window.confirm('Delete this brand?')) return;
    await axios.delete(`${API}/masters/brands/${id}`, { headers: authHeader() });
    setData(d => ({ ...d, brands: d.brands.filter(b => b.id !== id) }));
  };

  const addEmp = async (e) => {
    e.preventDefault(); if (!newEmp.name.trim()) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/masters/employees`, newEmp, { headers: authHeader() });
      setData(d => ({ ...d, employees: [...d.employees, r.data] }));
      setNewEmp({ name: '', role: '', phone: '' });
    } catch { alert('Error adding employee'); } finally { setSaving(false); }
  };

  const delEmp = async (id) => {
    if (!window.confirm('Delete this employee?')) return;
    await axios.delete(`${API}/masters/employees/${id}`, { headers: authHeader() });
    setData(d => ({ ...d, employees: d.employees.filter(em => em.id !== id) }));
  };

  const addSize = async (e) => {
    e.preventDefault(); if (!newSize.trim()) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/masters/sizes`, { value: newSize.trim() }, { headers: authHeader() });
      setData(d => ({ ...d, sizes: [...d.sizes, r.data] }));
      setNewSize('');
    } catch { alert('Error adding size'); } finally { setSaving(false); }
  };

  const delSize = async (id) => {
    if (!window.confirm('Delete this size?')) return;
    await axios.delete(`${API}/masters/sizes/${id}`, { headers: authHeader() });
    setData(d => ({ ...d, sizes: d.sizes.filter(s => s.id !== id) }));
  };

  const inp = 'px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:border-gray-400 bg-white flex-1';
  const TABS = ['brands', 'employees', 'sizes', 'godowns'];

  return (
    <div className="px-6 py-6" data-testid="masters-page">
      <PageHeader icon={Settings} section="Settings" title="Masters" subtitle="Manage brands, employees and cylinder sizes" />
      {error && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 w-fit flex-wrap">
        {TABS.map(t => (
          <button key={t} data-testid={`masters-tab-${t}`} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-md text-[13px] font-semibold capitalize transition-all ${tab === t ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>{t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-10"><RefreshCw size={18} className="animate-spin text-gray-400 mx-auto" /></div>
      ) : (
        <>
          {/* BRANDS */}
          {tab === 'brands' && (
            <div className="space-y-4">
              {isAdmin && (
                <form onSubmit={addBrand} className="flex items-center gap-2">
                  <input data-testid="add-brand-input" value={newBrand} onChange={e => setNewBrand(e.target.value)} className={inp} placeholder="Brand name e.g. HP Gas" />
                  <button data-testid="add-brand-btn" type="submit" disabled={saving} className="btn-primary text-sm px-4 py-2 flex-shrink-0">
                    {saving ? <RefreshCw size={13} className="animate-spin" /> : '+ Add Brand'}
                  </button>
                </form>
              )}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left small-label text-gray-500">Brand Name</th>
                    <th className="px-4 py-3 text-left small-label text-gray-500">Added</th>
                    {isAdmin && <th className="px-4 py-3 w-12"></th>}
                  </tr></thead>
                  <tbody>
                    {data.brands.length === 0 ? (
                      <tr><td colSpan={3} className="text-center py-8 text-gray-400 text-[13px]">No brands yet — add one above.</td></tr>
                    ) : data.brands.map((b, i) => (
                      <tr key={b.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-[13px] font-medium">{b.name}</td>
                        <td className="px-4 py-3 text-[12px] text-gray-400">{b.created_at?.split('T')[0] || '—'}</td>
                        {isAdmin && <td className="px-4 py-3"><button onClick={() => delBrand(b.id)} className="text-gray-300 hover:text-red-500 transition-colors"><Package size={13} /></button></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">{data.brands.length} brands</div>
              </div>
            </div>
          )}

          {/* EMPLOYEES */}
          {tab === 'employees' && (
            <div className="space-y-4">
              {isAdmin && (
                <form onSubmit={addEmp} className="flex flex-wrap items-center gap-2">
                  <input data-testid="add-emp-name" value={newEmp.name} onChange={e => setNewEmp(n => ({...n, name: e.target.value}))} className={inp} placeholder="Full name" required />
                  <select value={newEmp.role} onChange={e => setNewEmp(n => ({...n, role: e.target.value}))}
                    className="px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:border-gray-400 bg-white">
                    <option value="">Role…</option>
                    {['Driver','Filler','Helper','Loader','Manager','Security','Other'].map(r => <option key={r}>{r}</option>)}
                  </select>
                  <input value={newEmp.phone} onChange={e => setNewEmp(n => ({...n, phone: e.target.value}))} className="px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:border-gray-400 bg-white w-36" placeholder="Phone" />
                  <button data-testid="add-emp-btn" type="submit" disabled={saving} className="btn-primary text-sm px-4 py-2 flex-shrink-0">
                    {saving ? <RefreshCw size={13} className="animate-spin" /> : '+ Add Employee'}
                  </button>
                </form>
              )}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left small-label text-gray-500">Name</th>
                    <th className="px-4 py-3 text-left small-label text-gray-500">Role</th>
                    <th className="px-4 py-3 text-left small-label text-gray-500">Phone</th>
                    {isAdmin && <th className="px-4 py-3 w-12"></th>}
                  </tr></thead>
                  <tbody>
                    {data.employees.length === 0 ? (
                      <tr><td colSpan={4} className="text-center py-8 text-gray-400 text-[13px]">No employees yet — add one above.</td></tr>
                    ) : data.employees.map((em) => (
                      <tr key={em.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-[13px] font-medium">{em.name}</td>
                        <td className="px-4 py-3"><span className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{em.role || '—'}</span></td>
                        <td className="px-4 py-3 text-[13px] text-gray-500">{em.phone || '—'}</td>
                        {isAdmin && <td className="px-4 py-3"><button onClick={() => delEmp(em.id)} className="text-gray-300 hover:text-red-500 transition-colors"><Package size={13} /></button></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">{data.employees.length} employees</div>
              </div>
            </div>
          )}

          {/* SIZES */}
          {tab === 'sizes' && (
            <div className="space-y-4">
              {isAdmin && (
                <form onSubmit={addSize} className="flex items-center gap-2">
                  <input data-testid="add-size-input" value={newSize} onChange={e => setNewSize(e.target.value)} className={inp} placeholder="e.g. 14.2 or 14.2 kg" />
                  <button data-testid="add-size-btn" type="submit" disabled={saving} className="btn-primary text-sm px-4 py-2 flex-shrink-0">
                    {saving ? <RefreshCw size={13} className="animate-spin" /> : '+ Add Size'}
                  </button>
                </form>
              )}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left small-label text-gray-500">Size</th>
                    <th className="px-4 py-3 text-left small-label text-gray-500">Added</th>
                    {isAdmin && <th className="px-4 py-3 w-12"></th>}
                  </tr></thead>
                  <tbody>
                    {data.sizes.length === 0 ? (
                      <tr><td colSpan={3} className="text-center py-8 text-gray-400 text-[13px]">No sizes yet — add one above.</td></tr>
                    ) : data.sizes.map((s) => (
                      <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-[13px] font-medium">{s.value}</td>
                        <td className="px-4 py-3 text-[12px] text-gray-400">{s.created_at?.split('T')[0] || '—'}</td>
                        {isAdmin && <td className="px-4 py-3"><button onClick={() => delSize(s.id)} className="text-gray-300 hover:text-red-500 transition-colors"><Package size={13} /></button></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">{data.sizes.length} sizes</div>
              </div>
            </div>
          )}

          {/* GODOWNS (read-only from company) */}
          {tab === 'godowns' && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <span className="small-label text-gray-500">Company Godowns</span>
                <span className="text-xs text-gray-400">Managed by Super Admin</span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {(company?.godowns || []).length === 0 ? (
                    <tr><td className="text-center py-8 text-gray-400 text-[13px]">No godowns configured.</td></tr>
                  ) : (company?.godowns || []).map((g, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 text-[13px] font-medium">{g}</td>
                      <td className="px-4 py-3"><span className="text-[11px] font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded">ACTIVE</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">{(company?.godowns || []).length} godowns</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Audit Logs ───────────────────────────────────────────── */
export function AuditLogs() {
  return (
    <div className="px-6 py-6">
      <PageHeader icon={Shield} section="Settings" title="Audit Logs" subtitle="System activity trail" />
      <PlaceholderTable columns={['Timestamp','User','Action','Resource','Details']} rowCount={10} />
    </div>
  );
}

/* ─── Sales & Marketing ─────────────────────────────────────── */
export function SalesMarketing() {
  return (
    <div className="px-6 py-6">
      <PageHeader icon={Megaphone} section="Settings" title="Sales & Marketing" subtitle="Campaigns and lead tracking" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Active Leads', value: '12' },
          { label: 'Converted (Sep)', value: '3' },
          { label: 'Avg Deal Size', value: '₹2.4L' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="small-label text-gray-400 mb-2">{s.label}</div>
            <div className="font-black text-2xl">{s.value}</div>
          </div>
        ))}
      </div>
      <PlaceholderTable columns={['Lead','Contact','Stage','Value ₹','Next Action']} rowCount={5} />
    </div>
  );
}
